import {
	ERROR_CODES,
	EXPERIENCE_LIST_QUERY_CUANDO_VALUES,
	type ExperienceAccessLabel,
	type ExperienceCatalogPreviewResponse,
	type ExperienceFormat,
	type ExperienceListQueryCuando,
	type ExperienceResponse,
	esExperienciaOficial,
	safeRedirect,
} from '@diario/common';
import { createFileRoute, Link, useLocation, useNavigate } from '@tanstack/react-router';
import { type TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CLASE_TEXTO_ERROR } from '../components/ui/alert';
import {
	CalendarioDePlanes,
	type DiaCalendarioDePlanes,
	type EstadoDiaCalendarioDePlanes,
} from '../components/ui/calendarioDePlanes';
import { EstadoError } from '../components/ui/estadoError';
// `MapaEspana` no se monta directamente aquí (lo monta `MapaDePlanes` por dentro): solo hace
// falta el tipo `ProvinciaDeMapaEspana` para tipar `provinciasParaElMapa` más abajo.
import { type ProvinciaDeMapaEspana } from '../components/ui/mapaEspana';
import { PROVINCIAS_CAJA_CANARIAS, PROVINCIAS_MAPA_ESPANA } from '../components/ui/mapaEspana.paths';
import { SelloEstado, type SelloEstadoFigura } from '../components/ui/selloEstado';
import {
	TablonDeCarteles,
	type TablonDeCartelesFila,
	type TablonDeCartelesSello,
} from '../components/ui/tablonDeCarteles';
import { useCategories } from '../features/categories/api/useCategories';
import { useExperienceStats } from '../features/experiences/api/useExperienceStats';
import { useExperiences } from '../features/experiences/api/useExperiences';
import { CarteleraDePlanes, type PlanEnCartelera } from '../features/experiences/components/catalogo/carteleraDePlanes';
import { type EjeDeFiltro, FiltrosCatalogo } from '../features/experiences/components/catalogo/filtrosCatalogo';
import { IndiceDeVistas } from '../features/experiences/components/catalogo/indiceDeVistas';
import { ListaDePlanes, type PlanEnLista } from '../features/experiences/components/catalogo/listaDePlanes';
import { MantenerPulsado } from '../features/experiences/components/catalogo/mantenerPulsado';
import {
	type EscalonLeyendaMapaDePlanes,
	MapaDePlanes,
} from '../features/experiences/components/catalogo/mapaDePlanes';
import { traducirError } from '../lib/errores';

/**
 * `/experiencias` (respuestas del cliente 2026-09-17, P-13/P-25/P-46 — «la miel en los labios»):
 * el catálogo de experiencias del cliente, ensamblando los primitivos que las oleadas anteriores
 * ya construyeron (`design-system/ui_kits/app/screens/planes.html`). Mudada desde
 * `routes/_autenticado/experiencias.tsx` (iteración transversal «diseño 2026-09-16»): esta ruta ya
 * NO cuelga de `_autenticado` — `GET /experiences` admite ahora sesión opcional (oleada anterior de
 * esta misma tarea, ver `features/experiences/api/useExperiences.ts`), así que quien llega sin
 * cuenta también ve el catálogo, tapado.
 *
 * Con sesión, `GET /experiences` responde el DTO básico completo de siempre y esta pantalla pinta
 * las cinco vistas de costumbre (tablón, planes, cartelera, mapa, calendario). Sin sesión, responde
 * una vista previa redactada (`experienceCatalogPreviewResponseSchema`, `@diario/common`): solo
 * `id`/`format`/`region`/`mes`/`confirmedCount` — ni título, ni fecha exacta, ni sitio, ni
 * organizador, ni participantes. `esListaCompleta` (más abajo) distingue una forma de la otra por
 * la presencia de `title` y decide entre este cuerpo (`PantallaExperiencias`) y
 * `VistaExperienciasTapadas`, la versión tapada: cada tarjeta lleva a crear cuenta, nunca a la
 * ficha (P-25: un plan «solo para miembros» se sigue viendo a quien no es miembro — deliberado, no
 * se filtra aquí ni en el servidor).
 */
export const Route = createFileRoute('/experiencias')({
	component: PantallaExperiencias,
	validateSearch: leerBusquedaExperiencias,
});

/** «Los 3 más cercanos» (`ListaDePlanes`, título literal del mock — no varía con el recuento real). */
const TOTAL_PLANES_MAS_CERCANOS = 3;
/** Los ocho `<li class="cartelera__item">` del mock (`planes.html:9194`, sin contar el muro). */
const TOTAL_PLANES_CARTELERA = 8;

// ───────────────────────────── Búsqueda en la URL (lectura laxa) ─────────────────────────────

/** Rango de edad del contrato (`edadField`, `contracts/experiences/index.ts`): 18-70. */
const EDAD_MIN = 18;
const EDAD_MAX = 70;

const CUANDO_VALIDOS = new Set<string>(EXPERIENCE_LIST_QUERY_CUANDO_VALUES);

interface BusquedaExperiencias {
	categoryId?: string;
	cuando?: ExperienceListQueryCuando;
	edad?: number;
	precioMaxCents?: number;
	region?: string;
	soloOficiales: boolean;
}

/** Entero dentro de `[min, max]`, aceptando también su representación en cadena — cualquier otra
 * cosa (vacío, `NaN`, fuera de rango) cae a `undefined`, nunca lanza. */
const leerEnteroEnRango = (valor: unknown, min: number, max: number): number | undefined => {
	const numero = typeof valor === 'number' ? valor : Number(valor);
	if (!Number.isFinite(numero)) {
		return undefined;
	}
	const entero = Math.trunc(numero);
	return entero >= min && entero <= max ? entero : undefined;
};

/**
 * Lectura **laxa** de `?soloOficiales=&categoryId=&cuando=&region=&edad=&precioMaxCents=`: nunca
 * lanza error, cualquier valor inválido cae al valor por defecto — mismo criterio que
 * `leerBusquedaBienvenida` (`routes/_autenticado/bienvenida.tsx`) y `leerBusquedaLogin`
 * (`routes/login.tsx`). `categoryId`/`region` no se validan más allá de "es una cadena no vacía":
 * un valor que el backend acabe rechazando (p. ej. un UUID mal formado) se resuelve como una
 * respuesta de error normal de `useExperiences`, no como una excepción de esta función.
 */
function leerBusquedaExperiencias(busqueda: Record<string, unknown>): BusquedaExperiencias {
	return {
		categoryId:
			typeof busqueda.categoryId === 'string' && busqueda.categoryId.length > 0 ? busqueda.categoryId : undefined,
		cuando:
			typeof busqueda.cuando === 'string' && CUANDO_VALIDOS.has(busqueda.cuando)
				? (busqueda.cuando as ExperienceListQueryCuando)
				: undefined,
		edad: leerEnteroEnRango(busqueda.edad, EDAD_MIN, EDAD_MAX),
		precioMaxCents: leerEnteroEnRango(busqueda.precioMaxCents, 0, Number.MAX_SAFE_INTEGER),
		region: typeof busqueda.region === 'string' && busqueda.region.length > 0 ? busqueda.region : undefined,
		soloOficiales: busqueda.soloOficiales === true || busqueda.soloOficiales === 'true',
	};
}

// ───────────────────────────── Geometría de provincias (mapa) ─────────────────────────────

/** Las 52 provincias vendorizadas (`mapaEspana.paths.ts`, NO se edita): las dos claves cruzadas
 * (nombre ↔ ISO) que hacen falta para casar `region` (texto libre del catálogo de localizaciones,
 * p. ej. `"Zaragoza"`) con la `clave` que `MapaEspana`/`MapaDePlanes` necesitan para pintar. No
 * existe ningún catálogo de mapeo en `common/` para esto — decisión propia de esta tarea, ver el
 * informe final. */
const TODAS_LAS_PROVINCIAS = [...PROVINCIAS_MAPA_ESPANA, ...PROVINCIAS_CAJA_CANARIAS];
const CLAVE_POR_NOMBRE_PROVINCIA = new Map(
	TODAS_LAS_PROVINCIAS.map(provincia => [provincia.nombre, provincia.clave] as const),
);
const NOMBRE_POR_CLAVE_PROVINCIA = new Map(
	TODAS_LAS_PROVINCIAS.map(provincia => [provincia.clave, provincia.nombre] as const),
);

// ───────────────────────────── Fechas: helpers puros ─────────────────────────────

/** Clave estable `AAAA-MM-DD` en horario LOCAL (nunca `toISOString`, que desplaza a UTC). */
const formatearClaveIsoLocal = (fecha: Date): string => {
	const anio = fecha.getFullYear();
	const mes = String(fecha.getMonth() + 1).padStart(2, '0');
	const dia = String(fecha.getDate()).padStart(2, '0');
	return `${anio}-${mes}-${dia}`;
};

const inicioDeMes = (fecha: Date): Date => new Date(fecha.getFullYear(), fecha.getMonth(), 1);
const inicioDeHoy = (): Date => {
	const hoy = new Date();
	return new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
};
const sumarMeses = (fecha: Date, delta: number): Date => new Date(fecha.getFullYear(), fecha.getMonth() + delta, 1);
const mismoMes = (fecha: Date, mes: Date): boolean =>
	fecha.getFullYear() === mes.getFullYear() && fecha.getMonth() === mes.getMonth();

const DIAS_POR_SEMANA = 7;

interface DiaDeLaRejilla {
	clave: string;
	deOtroMes: boolean;
	fecha: Date;
	numero: number;
}

/**
 * Calcula la rejilla de semanas completas de un mes (huecos de relleno del mes anterior y
 * siguiente incluidos): función **pura** del módulo, sin dependencias de red ni de i18n, extraída
 * aparte para poder probarla — `CalendarioDePlanes` no calcula nada por dentro (su propio JSDoc),
 * así que este cálculo tiene que vivir en quien lo monta.
 */
export function construirRejillaDelMes(primerDiaDelMes: Date): DiaDeLaRejilla[] {
	const anio = primerDiaDelMes.getFullYear();
	const mes = primerDiaDelMes.getMonth();
	const ultimoDiaDelMes = new Date(anio, mes + 1, 0).getDate();
	// `getDay()`: 0=domingo..6=sábado. Se traslada a 0=lunes..6=domingo (la semana del contrato
	// empieza en lunes, `nombresDeDia` de `CalendarioDePlanesProps`).
	const primerDiaSemana = (new Date(anio, mes, 1).getDay() + DIAS_POR_SEMANA - 1) % DIAS_POR_SEMANA;

	const dias: DiaDeLaRejilla[] = [];
	const agregar = (fecha: Date, deOtroMes: boolean): void => {
		dias.push({ clave: formatearClaveIsoLocal(fecha), deOtroMes, fecha, numero: fecha.getDate() });
	};

	for (let i = primerDiaSemana; i > 0; i -= 1) {
		agregar(new Date(anio, mes, 1 - i), true);
	}
	for (let dia = 1; dia <= ultimoDiaDelMes; dia += 1) {
		agregar(new Date(anio, mes, dia), false);
	}
	const relleno = (DIAS_POR_SEMANA - (dias.length % DIAS_POR_SEMANA)) % DIAS_POR_SEMANA;
	for (let i = 1; i <= relleno; i += 1) {
		agregar(new Date(anio, mes + 1, i), true);
	}
	return dias;
}

const ENERO = 0;
const DIA_1 = 1;
/** Lunes 1 de enero de 2024 (UTC): fecha de referencia para generar los nombres de columna del
 * calendario con `Intl.DateTimeFormat` en vez de una lista hardcodeada — ninguna cadena de
 * interfaz vive aquí, así que `i18next-cli lint` no tiene nada que marcar. */
// biome-ignore lint/style/noMagicNumbers: el año de referencia no tiene mejor nombre que su propio valor.
const REFERENCIA_LUNES_UTC = Date.UTC(2024, ENERO, DIA_1);
// biome-ignore lint/style/noMagicNumbers: horas/minutos/segundos/milisegundos de un día, ya lo dice el propio nombre.
const MS_POR_DIA = 24 * 60 * 60 * 1000;

/** Los siete nombres de columna (lunes a domingo) en el idioma activo, vía `Intl`. */
const nombresDeDiaSemana = (idioma: string): readonly [string, string, string, string, string, string, string] => {
	const formateador = new Intl.DateTimeFormat(idioma, { timeZone: 'UTC', weekday: 'narrow' });
	const nombres = Array.from({ length: DIAS_POR_SEMANA }, (_valor, indice) =>
		formateador.format(new Date(REFERENCIA_LUNES_UTC + indice * MS_POR_DIA)),
	);
	return nombres as [string, string, string, string, string, string, string];
};

// ───────────────────────────── Formateo: fechas y precios (Intl, nunca a mano) ─────────────────────────────

const formatearFechaHora = (iso: string, idioma: string): string => {
	const fecha = new Date(iso);
	const dia = new Intl.DateTimeFormat(idioma, { day: 'numeric', weekday: 'long' }).format(fecha);
	const hora = new Intl.DateTimeFormat(idioma, { hour: '2-digit', minute: '2-digit' }).format(fecha);
	return `${dia} · ${hora}`;
};

const formatearDiaCorto = (iso: string, idioma: string): string =>
	new Intl.DateTimeFormat(idioma, { day: 'numeric', month: 'short' }).format(new Date(iso)).toUpperCase();

const formatearRangoFechas = (
	inicio: string | null | undefined,
	fin: string | null | undefined,
	idioma: string,
): string | undefined => {
	if (!inicio) {
		return undefined;
	}
	const inicioFmt = formatearDiaCorto(inicio, idioma);
	if (!fin || fin === inicio) {
		return inicioFmt;
	}
	return `${inicioFmt} - ${formatearDiaCorto(fin, idioma)}`;
};

const formatearZona = (zona: { region: string | null; town: string | null } | null | undefined): string | undefined => {
	if (!zona) {
		return undefined;
	}
	const partes = [zona.town, zona.region].filter((parte): parte is string => Boolean(parte));
	return partes.length > 0 ? partes.join(', ') : undefined;
};

/** `priceCents` viaja en céntimos (contrato `experienceResponseSchema`); `Intl.NumberFormat`
 * espera la unidad principal de la divisa. */
const CENTIMOS_POR_UNIDAD = 100;

const formatearPrecio = (
	priceCents: number | null | undefined,
	currency: string,
	idioma: string,
	textoGratis: string,
): string => {
	if (priceCents === null || priceCents === undefined || priceCents === 0) {
		return textoGratis;
	}
	return new Intl.NumberFormat(idioma, { currency, style: 'currency' }).format(priceCents / CENTIMOS_POR_UNIDAD);
};

// ───────────────────────────── Sellos y estado de un plan ─────────────────────────────

interface DatosSello {
	figura: SelloEstadoFigura;
	mio?: boolean;
	texto: string;
	voz: string;
}

/** `false` para `Date`s que no llegaron (sin `startDate`): nunca se pinta como "pasado" lo que no
 * tiene fecha conocida. */
const experienciaYaPaso = (experiencia: ExperienceResponse, ahora: Date): boolean =>
	Boolean(experiencia.startDate) && new Date(experiencia.startDate as string) < ahora;

const datosSelloPlan = (
	experiencia: ExperienceResponse,
	ahora: Date,
	t: TFunction<'experiences'>,
): DatosSello | undefined => {
	if (experienciaYaPaso(experiencia, ahora)) {
		return { figura: 'historia', texto: t('catalogo.sellos.pasado.texto'), voz: t('catalogo.sellos.pasado.voz') };
	}
	if (experiencia.viewerJoinStatus === 'CONFIRMED') {
		return {
			figura: 'check',
			mio: true,
			texto: t('catalogo.sellos.apuntado.texto'),
			voz: t('catalogo.sellos.apuntado.voz'),
		};
	}
	if (experiencia.viewerJoinStatus === 'REQUESTED') {
		return {
			figura: 'reloj',
			mio: true,
			texto: t('catalogo.sellos.pedido.texto'),
			voz: t('catalogo.sellos.pedido.voz'),
		};
	}
	if (experiencia.capacity != null && experiencia.confirmedCount >= experiencia.capacity) {
		return { figura: 'rayado', texto: t('catalogo.sellos.lleno.texto'), voz: t('catalogo.sellos.lleno.voz') };
	}
	return undefined;
};

type PlanCardEstadoCatalogo = 'apuntado' | 'lista-espera' | 'lleno' | 'normal' | 'pasado' | 'pedido' | 'vivido';

const estadoPlanDesdeExperiencia = (experiencia: ExperienceResponse, ahora: Date): PlanCardEstadoCatalogo => {
	if (experiencia.viewerJoinStatus === 'ATTENDED') {
		return 'vivido';
	}
	if (experienciaYaPaso(experiencia, ahora)) {
		return 'pasado';
	}
	if (experiencia.viewerJoinStatus === 'CONFIRMED') {
		return 'apuntado';
	}
	if (experiencia.viewerJoinStatus === 'REQUESTED') {
		return 'pedido';
	}
	if (experiencia.viewerJoinStatus === 'WAITLISTED') {
		return 'lista-espera';
	}
	if (experiencia.capacity != null && experiencia.confirmedCount >= experiencia.capacity) {
		return 'lleno';
	}
	return 'normal';
};

/** Las cinco etiquetas de acceso (`EXPERIENCE_ACCESS_LABELS`, contrato congelado) son un
 * enumerado cerrado y corto: `Record` de llamadas `t()` literales, nunca una plantilla indexada
 * por variable (`docs/i18n.md` § «La alternativa al `preservePatterns`»). Reutiliza
 * `enumerados.etiquetaAcceso.*`, que ya existe en este namespace. */
const etiquetaAccesoPorValor = (t: TFunction<'experiences'>): Record<ExperienceAccessLabel, string> => ({
	BRAND: t('enumerados.etiquetaAcceso.BRAND'),
	COLLABORATOR: t('enumerados.etiquetaAcceso.COLLABORATOR'),
	MEMBERS_ONLY: t('enumerados.etiquetaAcceso.MEMBERS_ONLY'),
	OFFICIAL: t('enumerados.etiquetaAcceso.OFFICIAL'),
	PRIVATE: t('enumerados.etiquetaAcceso.PRIVATE'),
});

const formatearPlazas = (
	confirmedCount: number,
	capacity: number | null | undefined,
	t: TFunction<'experiences'>,
): string | undefined =>
	capacity == null ? undefined : t('catalogo.planes.plazas', { capacidad: capacity, confirmadas: confirmedCount });

const construirOrganizador = (experiencia: ExperienceResponse): string | undefined => {
	if (experiencia.organizerUsername) {
		return `@${experiencia.organizerUsername}`;
	}
	return experiencia.organizerFullName ?? undefined;
};

// ───────────────────────────── Rampa de calor del mapa ─────────────────────────────

const NIVEL_MAXIMO_MAPA = 5;

/** Bucket 0-5 a partir del número real de planes abiertos, relativo al máximo del listado —
 * nunca una cifra inventada: solo reparte las cifras reales de `useExperienceStats` en una
 * escala visual de 6 escalones (mismo criterio que cualquier leyenda de mapa de calor). */
const nivelDeCalor = (abiertos: number, maxAbiertos: number): 0 | 1 | 2 | 3 | 4 | 5 => {
	if (abiertos <= 0 || maxAbiertos <= 0) {
		return 0;
	}
	const nivel = Math.ceil((abiertos / maxAbiertos) * NIVEL_MAXIMO_MAPA);
	return Math.min(NIVEL_MAXIMO_MAPA, Math.max(1, nivel)) as 0 | 1 | 2 | 3 | 4 | 5;
};

/** Los cuatro presets fijos del eje «Cuánto quieres pagar» (mockup `planes.html`, sección
 * «Afinamos por lo que vives»): `precioMaxCents` es un número libre en el contrato
 * (`experienceListQuerySchema`), pero el mock lo expone como chips de valores fijos, no como un
 * rango — se traduce aquí, en la ruta, a un pequeño catálogo cerrado de tres presets con cifra
 * (más «Lo que sea», que limpia el filtro). */
const PRECIO_PRESETS = [
	{ cents: 0, id: 'gratis' },
	{ cents: 1500, id: 'hasta15' },
	{ cents: 3000, id: 'hasta30' },
] as const;

/** Enumerado cerrado y corto (los tres presets + «cualquiera»): `Record` de llamadas `t()`
 * literales, nunca una llamada a `t` indexada con el identificador del preset como variable — el
 * extractor estático de `i18next-cli` no ve una clave así construida en tiempo de ejecución
 * (`docs/i18n.md` § «La alternativa al `preservePatterns`»). */
const etiquetasPrecioPorValor = (
	t: TFunction<'experiences'>,
): Record<'cualquiera' | 'gratis' | 'hasta15' | 'hasta30', string> => ({
	cualquiera: t('catalogo.filtros.precio.opciones.cualquiera'),
	gratis: t('catalogo.filtros.precio.opciones.gratis'),
	hasta15: t('catalogo.filtros.precio.opciones.hasta15'),
	hasta30: t('catalogo.filtros.precio.opciones.hasta30'),
});

// ───────────────────────────── Filtros: un eje sin opciones no se pinta ─────────────────────────────

/**
 * Descarta los ejes de tipo `chips` que lleguen sin opciones — mismo criterio que «las CTA sin
 * destino no se pintan» (ADR-0022, ver `components/ui/missionCard.tsx`) aplicado a un grupo de
 * filtro: un `<fieldset>` con su `<legend>` y cero chips (p. ej. «Zona» cuando ninguna experiencia
 * publicada tiene `location.region`, o el catálogo de provincias llega con `planes: 0` en las 50)
 * es un control inútil, no un control vacío legítimo. Los ejes de tipo `rango` (hoy solo «Tu
 * edad») se conservan siempre: no tienen `opciones`, así que "vacío" no les aplica — un rango sin
 * selección todavía es un control perfectamente usable (cae al punto medio, `etiquetaValor` pinta
 * el guion).
 *
 * Este bloque no puede quedarse sin NINGÚN eje de chips que pintar: «Cuándo» (`ejeCuando`) y
 * «Cuánto quieres pagar» (`ejePrecio`) están construidos a partir de catálogos fijos del propio
 * módulo (`EXPERIENCE_LIST_QUERY_CUANDO_VALUES` y `PRECIO_PRESETS` + el preset «cualquiera»), no
 * de datos que puedan llegar vacíos de la red — nunca son `undefined` ni tienen `opciones.length
 * === 0`. Por eso no hace falta lógica aparte para ocultar el bloque `FiltrosCatalogo` entero: como
 * mínimo esos dos ejes siempre sobreviven a este filtro.
 */
const descartarEjesDeChipsVacios = (ejes: EjeDeFiltro[]): EjeDeFiltro[] =>
	ejes.filter(eje => eje.tipo !== 'chips' || eje.opciones.length > 0);

// ───────────────────────────── Redacción sin sesión (P-13, «la miel en los labios») ─────────────────────────────

/**
 * `GET /experiences` sin sesión responde `ExperienceCatalogPreviewResponse[]` (vista previa
 * redactada: solo `id`/`format`/`region`/`mes`/`confirmedCount`) en vez del DTO básico completo de
 * siempre. `title` es la única clave que la vista previa JAMÁS trae (es obligatoria en
 * `experienceResponseSchema`, así que su presencia es una señal fiable de qué forma llegó). Una
 * lista vacía se trata como "completa" — no hay ningún dato que tapar ni que filtrar en un array
 * vacío, así que las dos formas son indistinguibles (e inofensivas) con cero elementos.
 */
export const esListaCompleta = (
	items: ExperienceCatalogPreviewResponse[] | ExperienceResponse[],
): items is ExperienceResponse[] => {
	const primero = items[0];
	return primero === undefined || 'title' in primero;
};

/** Nombre del mes (1-12) en el idioma activo, vía `Intl` — mismo criterio que `nombresDeDiaSemana`
 * más arriba (año de referencia arbitrario: ningún nombre de mes cambia con el año). La vista
 * previa sin sesión solo trae el mes, nunca el día ni el año: esta pantalla no finge una fecha más
 * precisa de la que en realidad tiene. */
const ANIO_REFERENCIA_MES = 2024;
const nombreDeMes = (mes: number, idioma: string): string =>
	new Intl.DateTimeFormat(idioma, { month: 'long' }).format(new Date(ANIO_REFERENCIA_MES, mes - 1, DIA_1));

/** Los siete formatos (`EXPERIENCE_FORMATS`, contrato congelado) son un enumerado cerrado y corto:
 * mismo molde que `etiquetaAccesoPorValor` más arriba — `Record` de llamadas `t()` literales, nunca
 * una plantilla indexada por variable (`docs/i18n.md` § «La alternativa al `preservePatterns`»).
 * Reutiliza `enumerados.formato.*`, que ya existe en este namespace. Es el único dato de "tipo de
 * plan" que la vista previa sin sesión trae, así que hace de título de la tarjeta tapada. */
const etiquetaFormatoPorValor = (t: TFunction<'experiences'>): Record<ExperienceFormat, string> => ({
	ACAMPADA: t('enumerados.formato.ACAMPADA'),
	CASA_RURAL: t('enumerados.formato.CASA_RURAL'),
	ESCAPADA: t('enumerados.formato.ESCAPADA'),
	PLAN: t('enumerados.formato.PLAN'),
	RETIRO: t('enumerados.formato.RETIRO'),
	TALLER: t('enumerados.formato.TALLER'),
	VIAJE: t('enumerados.formato.VIAJE'),
});

/** Mismas clases que usa `ListaDePlanes` (fichero prohibido en esta tarea,
 * `features/experiences/components/catalogo/listaDePlanes.tsx`) para su propio titular y su
 * `<ul>` — no las exporta, así que se replican aquí en vez de importarlas. */
const CLASE_TITULO_SECCION_TAPADA = 'm-0 mb-4 font-bold text-gray-ink text-xs uppercase tracking-label';
const CLASE_LISTA_TAPADA = 'm-0 flex list-none flex-col gap-5 p-0';

/**
 * Mismo lenguaje visual que `PlanCard` en estado `normal` (fichero prohibido,
 * `components/ui/planCard.tsx`) más el foco/hover accesibles de su propio control «ver la ficha»:
 * a diferencia de una `PlanCard` real, aquí TODA la tarjeta es el enlace, no solo un control
 * interno — `PlanEnLista`/`PlanCard` solo exponen `href` (una cadena plana) para navegar, y
 * `to`/`search` tipados es lo único que sabe construir un `?redirect=` correcto hacia `/login`
 * (P-13: «al tocar, se pide cuenta, guardando el destino para volver»); incrustar un query string a
 * mano dentro de `href` no funciona con este router (`resolvePath` trata todo el `to` como
 * pathname, sin separar el `?`). Por eso esta tarjeta no puede montarse con el primitivo
 * compartido y se rehace aquí, mínima, con el mismo lenguaje visual — ver el informe final de esta
 * tarea.
 */
const CLASE_TARJETA_TAPADA =
	'block rounded-md border border-border bg-paper text-ink no-underline hover:shadow-card-hover motion-safe:transition-[transform,box-shadow] motion-safe:duration motion-safe:ease motion-safe:hover:-translate-y-[2px] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-focus-ring';

interface VistaExperienciasTapadasProps {
	/** `experiencias.isFetching` (mismo criterio que `actualizandoCatalogo` de `PantallaExperiencias`). */
	actualizando: boolean;
	/** Destino guardado en `?redirect=` antes de mandar a `/login` — `useLocation({ select: … })`,
	 * sin sanear todavía (lo sanea esta misma vista con `safeRedirect`, mismo criterio que
	 * `routes/unirse.$id.tsx`). */
	destino: string;
	ejes: EjeDeFiltro[];
	/** Aviso YA traducido de una actualización fallida con datos previos en pantalla (oleada 2,
	 * corrección de rate limiting — ver el JSDoc de `errorActualizacionCatalogo` en
	 * `PantallaExperiencias`), o `undefined` sin fallo que avisar. */
	errorActualizacion: string | undefined;
	idioma: string;
	items: readonly ExperienceCatalogPreviewResponse[];
	leyendaMapa: EscalonLeyendaMapaDePlanes[];
	notaMapa: string;
	onCambioFiltro: (clave: string, valor: string | number | null) => void;
	provinciasParaElMapa: ProvinciaDeMapaEspana[];
	t: TFunction<'experiences'>;
}

/**
 * La versión tapada del catálogo (respuestas del cliente 2026-09-17, P-13, «la miel en los
 * labios»): sin sesión solo llegan cinco cosas por plan (`id`/`format`/`region`/`mes`/
 * `confirmedCount`), así que aquí NO caben el tablón, la cartelera ni el calendario — los tres
 * necesitan la fecha exacta (día, no solo mes) y el título, que esta vista nunca recibe; pintarlos
 * con huecos o con datos inventados violaría la propia regla de esta tarea («si un dato no viene,
 * no se enseña»). Se quedan las dos secciones que SÍ tienen datos reales que mostrar: la lista de
 * tarjetas tapadas y el mapa (agregados por provincia, ya públicos sin sesión desde antes de esta
 * tarea — `useExperienceStats`). Los filtros siguen activos: son parámetros de la petición, nunca
 * datos de la respuesta, así que no hay nada que redactar en ellos.
 */
function VistaExperienciasTapadas({
	actualizando,
	destino,
	ejes,
	errorActualizacion,
	idioma,
	items,
	leyendaMapa,
	notaMapa,
	onCambioFiltro,
	provinciasParaElMapa,
	t,
}: VistaExperienciasTapadasProps): React.JSX.Element {
	const destinoSeguro = safeRedirect(destino);
	const etiquetasFormato = etiquetaFormatoPorValor(t);

	return (
		<div className='flex flex-col gap-8'>
			<IndiceDeVistas
				etiquetaNav={t('catalogo.indice.etiquetaNav')}
				vistas={[
					{ ancla: 'pl-planes', etiqueta: t('catalogo.indice.vistas.planes') },
					{ ancla: 'pl-mapa', etiqueta: t('catalogo.indice.vistas.mapa') },
				]}
			/>

			<header className='flex flex-col gap-2'>
				<h1 className='font-display font-black text-3xl text-ink'>{t('catalogo.titulo')}</h1>
				<p className='text-base text-gray-ink'>
					{t('catalogo.subtitulo')} <em>{t('catalogo.subtituloEnfasis')}</em>
				</p>
				{actualizando ? (
					<p aria-live='polite' className='text-gray-ink text-sm' role='status'>
						{t('catalogo.estados.actualizando')}
					</p>
				) : null}
				{!actualizando && errorActualizacion !== undefined ? (
					<p aria-live='polite' className={`text-sm ${CLASE_TEXTO_ERROR}`} role='status'>
						{errorActualizacion}
					</p>
				) : null}
			</header>

			<FiltrosCatalogo ejes={ejes} onCambio={onCambioFiltro} titulo={t('catalogo.filtros.titulo')} />

			<section aria-busy={actualizando} className='scroll-mt-24' id='pl-planes'>
				<h2 className={CLASE_TITULO_SECCION_TAPADA}>{t('catalogo.indice.vistas.planes')}</h2>
				<ul className={CLASE_LISTA_TAPADA}>
					{items.map(experiencia => (
						<li key={experiencia.id}>
							<Link
								aria-label={t('catalogo.sinFicha.comoSeEntra')}
								className={CLASE_TARJETA_TAPADA}
								search={{ modo: 'crearCuenta', redirect: destinoSeguro }}
								to='/login'
							>
								<div className='flex items-start gap-4 p-4'>
									<div className='min-w-0 flex-1'>
										<h3 className='m-0 mb-1 font-adventure font-bold text-ink text-md leading-snug tracking-snug'>
											{etiquetasFormato[experiencia.format]}
										</h3>
										<div className='text-gray-ink text-sm'>
											{[nombreDeMes(experiencia.mes, idioma), experiencia.region].filter(Boolean).join(' · ')}
										</div>
										<div className='mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs'>
											<span className='rounded-pill bg-paper-2 px-2 py-0.5 font-medium text-gray-ink'>
												{t('catalogo.sinFicha.personasDentro', { count: experiencia.confirmedCount })}
											</span>
										</div>
									</div>
								</div>
								<div className='rounded-b-md border-border border-t bg-grumete-tint px-4 py-2 text-ink-warm text-xs'>
									<p className='m-0'>{t('catalogo.sinFicha.invitacion')}</p>
									<p className='m-0 mt-1 font-medium text-accent-ink'>{t('catalogo.sinFicha.comoSeEntra')}</p>
								</div>
							</Link>
						</li>
					))}
				</ul>
			</section>

			<div className='scroll-mt-24' id='pl-mapa'>
				<MapaDePlanes
					etiquetaCanarias={t('catalogo.mapa.etiquetaCanarias')}
					leyenda={leyendaMapa}
					nota={notaMapa}
					onSeleccionarProvincia={clave => {
						const region = NOMBRE_POR_CLAVE_PROVINCIA.get(clave);
						if (region) {
							onCambioFiltro('region', region);
						}
					}}
					provincias={provinciasParaElMapa}
					titulo={t('catalogo.mapa.titulo')}
					tituloAccesibleMapa={t('catalogo.mapa.tituloAccesible')}
				/>
			</div>
		</div>
	);
}

// ───────────────────────────── Componente ─────────────────────────────

function PantallaExperiencias(): React.JSX.Element {
	// Oleada 2 (corrección de rate limiting): namespace `common` sumado al propio (mismo criterio
	// que `centro.tsx`/`tablonExperiencia.tsx`/`unirse.$id.tsx`, `useTranslation([propio, 'common'])`)
	// para que `t` tipe `common:errores.ERR-007` sin ensanchar el tipo a cualquier clave.
	const { i18n, t } = useTranslation(['experiences', 'common']);
	const navigate = useNavigate({ from: Route.fullPath });
	const busqueda = Route.useSearch();
	// Destino a guardar en `?redirect=` antes de mandar a `/login` desde una tarjeta tapada (pathname
	// + query + hash) — mismo mecanismo que `routes/unirse.$id.tsx` (`SalidaCrearCuenta`).
	const destino = useLocation({ select: location => location.href });

	const [mesTablon, establecerMesTablon] = useState(() => inicioDeMes(new Date()));
	const [mesCalendario, establecerMesCalendario] = useState(() => inicioDeMes(new Date()));
	const [carteleraRevelada, establecerCarteleraRevelada] = useState(false);

	const experiencias = useExperiences({
		categoryId: busqueda.categoryId,
		cuando: busqueda.cuando,
		edad: busqueda.edad,
		limit: 100,
		precioMaxCents: busqueda.precioMaxCents,
		region: busqueda.region,
		soloOficiales: busqueda.soloOficiales ? true : undefined,
	});
	const categorias = useCategories();
	const stats = useExperienceStats();

	/** Cambia UN eje de filtro en la URL (chips/rango de `FiltrosCatalogo`) — mismo criterio que
	 * `leerBusquedaExperiencias`: nunca lanza, una clave desconocida no hace nada.
	 *
	 * `resetScroll: false`: por defecto TanStack Router lleva el scroll a 0 en CUALQUIER `navigate`
	 * (`router-core`, `this._scroll.next = next.resetScroll ?? true`), incluso una navegación a la
	 * misma ruta que solo cambia el search — no depende de que cambie la altura del documento. Sin
	 * este flag, el salto de scroll de la auditoría en el navegador sobrevivía a la corrección de
	 * `placeholderData: keepPreviousData` (el árbol ya no se desmonta, pero el scroll seguía
	 * yéndose a 0 en cada chip). `irAFicha`, más abajo, navega a OTRA ruta (`/unirse/$id`): ahí sí
	 * queda el scroll a 0 por defecto, que es lo esperado al abrir una pantalla nueva. */
	const manejarCambioFiltro = (clave: string, valor: string | number | null): void => {
		void navigate({
			resetScroll: false,
			search: previa => {
				const siguiente = { ...previa };
				if (clave === 'categoryId') {
					siguiente.categoryId = valor === null ? undefined : String(valor);
				} else if (clave === 'cuando') {
					siguiente.cuando = valor === null ? undefined : (valor as ExperienceListQueryCuando);
				} else if (clave === 'region') {
					siguiente.region = valor === null ? undefined : String(valor);
				} else if (clave === 'edad') {
					siguiente.edad = valor === null ? undefined : Number(valor);
				} else if (clave === 'precio') {
					const preset = PRECIO_PRESETS.find(opcion => opcion.id === valor);
					siguiente.precioMaxCents = preset ? preset.cents : undefined;
				}
				return siguiente;
			},
		});
	};

	const irAFicha = (id: string): void => {
		void navigate({ params: { id }, to: '/unirse/$id' });
	};

	// ── Estados de carga y error de la pantalla entera (H-12): `EstadoError`, nunca `Alert` a mano.
	//
	// El desmontaje al filtrar (auditoría en el navegador: catálogo entero en blanco ~110 ms y
	// scroll a cero en cada chip) venía de mirar `isPending`. Con `placeholderData: keepPreviousData`
	// en `useExperiences` (`features/experiences/api/useExperiences.ts`), cada cambio de filtro sigue
	// disparando una petición nueva, pero `data` conserva la página anterior mientras llega — así que
	// el árbol entero ya no tiene por qué sustituirse por el esqueleto. La condición correcta para
	// "primera carga, todavía sin nada que enseñar" es que `data` siga sin definir, no `isPending`
	// (que con el flag puesto deja de ser cierto en cuanto hay un dato previo que ofrecer de
	// placeholder). Un cambio de filtro que sí falla, sin ningún dato previo de ninguna clave, cae
	// igual que un fallo en la primera carga.
	if (experiencias.data === undefined) {
		if (experiencias.isError) {
			// Oleada 2 (corrección de rate limiting): un 429 (`ERR-007`) no es un fallo reintentable
			// como el resto — pulsar «Reintentar» golpearía de nuevo el mismo endpoint ya limitado
			// (mismo criterio que `routes/unirse.$id.tsx`), así que esta rama oculta solo ese botón;
			// la salida al inicio sigue disponible igual que en cualquier otro fallo real. El mensaje
			// sale de `traducirError` (`lib/errores.ts`, oleada 1) sobre `common:errores.ERR-007`, ya
			// existente en los dos idiomas antes de esta tarea.
			const limitado = traducirError(experiencias.error) === ERROR_CODES.rateLimited;
			return (
				<EstadoError
					mensaje={limitado ? t(`common:errores.${ERROR_CODES.rateLimited}`) : t('catalogo.estados.error')}
					reintentar={
						limitado
							? undefined
							: { etiqueta: t('ficha.estados.reintentar'), onClick: () => void experiencias.refetch() }
					}
					salida={{ etiqueta: t('ficha.estados.salida'), to: '/' }}
					variante='error'
				/>
			);
		}
		return <EstadoError mensaje={t('catalogo.estados.cargando')} variante='carga' />;
	}

	// Actualización EN LÍNEA (mismo defecto de la auditoría): con datos previos ya en pantalla, un
	// cambio de filtro deja `experiencias.isFetching` en `true` sin tocar `data` (todavía el
	// `placeholderData` de la página anterior) — la señal de "se está actualizando" se pinta sin
	// sustituir nada del árbol, y las secciones construidas a partir de `items` (más abajo) llevan
	// `aria-busy` mientras dura, para quien usa lector de pantalla.
	const actualizandoCatalogo = experiencias.isFetching;

	// El agujero de `keepPreviousData` (oleada 2, corrección de rate limiting): con datos previos
	// ya en pantalla, un cambio de filtro que ACABA fallando (p. ej. un 429 tras agotar el cubo)
	// deja `isFetching` en `false` e `isError` en `true` sin que `data` se mueva de la página
	// anterior — antes de esta tarea, nada de eso se pintaba: el usuario se quedaba con la lista
	// vieja y, como mucho, un "Actualizando…" que nunca se resolvía. Un 429 reutiliza su propio
	// mensaje ya traducido (`common:errores.ERR-007`); cualquier otro código usa un aviso propio y
	// genérico (`catalogo.estados.errorActualizacion`) que no inventa un motivo que no se conoce.
	// Nunca sustituye nada del árbol (misma nota de `actualizandoCatalogo`, arriba): es un aviso
	// discreto junto a la cabecera, no un `EstadoError` a pantalla completa.
	let errorActualizacionCatalogo: string | undefined;
	if (experiencias.isError) {
		errorActualizacionCatalogo =
			traducirError(experiencias.error) === ERROR_CODES.rateLimited
				? t(`common:errores.${ERROR_CODES.rateLimited}`)
				: t('catalogo.estados.errorActualizacion');
	} else {
		errorActualizacionCatalogo = undefined;
	}

	const ahora = new Date();
	const items = experiencias.data.items;

	// ── 4. FiltrosCatalogo: los cinco ejes ──────────────────────────────────────────────────
	// Calculados aquí, ANTES de saber si la respuesta llegó completa o tapada (más abajo): ninguno
	// de los dos depende de `items` (solo de `categorias`/`stats`/`busqueda`), así que los reutilizan
	// tanto `PantallaExperiencias` (esta función) como `VistaExperienciasTapadas` sin duplicar la
	// lógica de filtros — los mismos ejes narran «qué hay» a quien tiene sesión y a quien no.
	const ejeAdn: EjeDeFiltro | undefined = categorias.data
		? {
				clave: 'categoryId',
				leyenda: t('catalogo.filtros.adn.leyenda'),
				opciones: categorias.data.items.map(categoria => ({
					etiqueta: `${categoria.emoji} ${categoria.name}`.trim(),
					valor: categoria.id,
				})),
				seleccion: busqueda.categoryId ?? null,
				tipo: 'chips',
			}
		: undefined;

	const etiquetasCuando: Record<ExperienceListQueryCuando, { etiqueta: string; pista: string }> = {
		ahora: {
			etiqueta: t('catalogo.filtros.cuando.opciones.ahora.etiqueta'),
			pista: t('catalogo.filtros.cuando.opciones.ahora.pista'),
		},
		finde: {
			etiqueta: t('catalogo.filtros.cuando.opciones.finde.etiqueta'),
			pista: t('catalogo.filtros.cuando.opciones.finde.pista'),
		},
		mes: {
			etiqueta: t('catalogo.filtros.cuando.opciones.mes.etiqueta'),
			pista: t('catalogo.filtros.cuando.opciones.mes.pista'),
		},
		semana: {
			etiqueta: t('catalogo.filtros.cuando.opciones.semana.etiqueta'),
			pista: t('catalogo.filtros.cuando.opciones.semana.pista'),
		},
	};
	const ejeCuando: EjeDeFiltro = {
		clave: 'cuando',
		leyenda: t('catalogo.filtros.cuando.leyenda'),
		opciones: EXPERIENCE_LIST_QUERY_CUANDO_VALUES.map(valor => ({
			etiqueta: etiquetasCuando[valor].etiqueta,
			pista: etiquetasCuando[valor].pista,
			valor,
		})),
		seleccion: busqueda.cuando ?? null,
		tipo: 'chips',
	};

	const provinciasConPlanes = stats.data ? stats.data.items.filter(provincia => provincia.planes > 0) : [];
	const ejeZona: EjeDeFiltro | undefined = stats.data
		? {
				clave: 'region',
				leyenda: t('catalogo.filtros.zona.leyenda'),
				opciones: [...provinciasConPlanes]
					.sort((a, b) => a.region.localeCompare(b.region, 'es'))
					.map(provincia => ({
						etiqueta: provincia.region,
						pista: t('catalogo.filtros.zona.pista', { count: provincia.planes }),
						valor: provincia.region,
					})),
				seleccion: busqueda.region ?? null,
				tipo: 'chips',
			}
		: undefined;

	const ejeEdad: EjeDeFiltro = {
		clave: 'edad',
		etiquetaControl: t('catalogo.filtros.edad.control'),
		etiquetaQuitar: t('catalogo.filtros.edad.quitar'),
		etiquetaValor: busqueda.edad === undefined ? t('catalogo.filtros.edad.sinElegir') : String(busqueda.edad),
		leyenda: t('catalogo.filtros.edad.leyenda'),
		max: EDAD_MAX,
		min: EDAD_MIN,
		tipo: 'rango',
		valor: busqueda.edad ?? null,
	};

	const seleccionPrecio = ((): string | null => {
		const preset = PRECIO_PRESETS.find(opcion => opcion.cents === busqueda.precioMaxCents);
		if (preset) {
			return preset.id;
		}
		return busqueda.precioMaxCents === undefined ? 'cualquiera' : null;
	})();
	const etiquetasPrecio = etiquetasPrecioPorValor(t);
	const ejePrecio: EjeDeFiltro = {
		clave: 'precio',
		leyenda: t('catalogo.filtros.precio.leyenda'),
		opciones: [
			...PRECIO_PRESETS.map(opcion => ({ etiqueta: etiquetasPrecio[opcion.id], valor: opcion.id })),
			{ etiqueta: etiquetasPrecio.cualquiera, valor: 'cualquiera' },
		],
		seleccion: seleccionPrecio,
		tipo: 'chips',
	};

	const ejesConDato = [ejeAdn, ejeCuando, ejeZona, ejeEdad, ejePrecio].filter(
		(eje): eje is EjeDeFiltro => eje !== undefined,
	);
	// H-defecto «Zona vacía»: un eje de chips sin opciones (p. ej. «Zona» cuando ninguna provincia
	// tiene planes) no se pinta — ver el JSDoc de `descartarEjesDeChipsVacios` más arriba.
	const ejes = descartarEjesDeChipsVacios(ejesConDato);

	// ── 9. MapaDePlanes: agregados reales por provincia ──────────────────────────────────────
	// Calculado aquí, junto a los ejes: tampoco depende de `items` (solo de `stats`), y
	// `VistaExperienciasTapadas` lo necesita igual que `PantallaExperiencias` — el mapa es agregado
	// por provincia, público sin sesión desde antes de esta tarea (`useExperienceStats`), así que no
	// hay nada que tapar en él.
	const provinciasStats = stats.data?.items ?? [];
	const maxAbiertos = provinciasStats.reduce((maximo, provincia) => Math.max(maximo, provincia.abiertos), 0);
	const provinciasParaElMapa: ProvinciaDeMapaEspana[] = provinciasStats.flatMap(provincia => {
		const clave = CLAVE_POR_NOMBRE_PROVINCIA.get(provincia.region);
		if (!clave) {
			return [];
		}
		let etiqueta: string;
		if (provincia.abiertos <= 0) {
			etiqueta = t('catalogo.mapa.provincia.sinAbiertos', { region: provincia.region });
		} else if (provincia.abiertos === provincia.planes) {
			etiqueta = t('catalogo.mapa.provincia.todosAbiertos', { count: provincia.planes, region: provincia.region });
		} else {
			etiqueta = t('catalogo.mapa.provincia.parcial', {
				abiertos: provincia.abiertos,
				planes: provincia.planes,
				region: provincia.region,
			});
		}
		return [{ clave, etiqueta, nivel: nivelDeCalor(provincia.abiertos, maxAbiertos) }];
	});
	const notaMapa =
		provinciasConPlanes.length > 0
			? t('catalogo.mapa.nota', { count: provinciasConPlanes.length })
			: t('catalogo.mapa.notaSinDatos');
	const leyendaMapa: EscalonLeyendaMapaDePlanes[] = [
		{ etiqueta: t('catalogo.mapa.leyenda.nadaTodavia'), nivel: 0 },
		{ etiqueta: t('catalogo.mapa.leyenda.puedesEntrar'), nivel: NIVEL_MAXIMO_MAPA },
	];

	// Sin sesión (vista previa redactada, P-13): el resto de esta función asume el DTO básico
	// completo (`experiencia.title`/`startDate`/`zone`/`accessLabel`…), que esa vista previa nunca
	// trae — se pinta la versión tapada en su lugar, con lo poco que SÍ hay (tipo, zona, mes,
	// recuento) más los ejes de filtro y el mapa, ya calculados arriba (ninguno de los dos depende
	// de `items`).
	if (!esListaCompleta(items)) {
		return (
			<VistaExperienciasTapadas
				actualizando={actualizandoCatalogo}
				destino={destino}
				ejes={ejes}
				errorActualizacion={errorActualizacionCatalogo}
				idioma={i18n.language}
				items={items}
				leyendaMapa={leyendaMapa}
				notaMapa={notaMapa}
				onCambioFiltro={manejarCambioFiltro}
				provinciasParaElMapa={provinciasParaElMapa}
				t={t}
			/>
		);
	}

	/**
	 * Tope de navegación de mes (tablón y calendario): el mes del último plan conocido del listado,
	 * nunca por debajo del mes actual. Los dos estados (`mesTablon`, `mesCalendario`) arrancan en el
	 * mes en curso, así que un tope fijado también en el mes en curso dejaba la flecha «mes
	 * siguiente» deshabilitada desde el primer render — no se podía ver ningún mes futuro en un
	 * catálogo de planes futuros. Se calcula una sola vez y se reutiliza en los dos sitios.
	 */
	const topeMes = items.reduce(
		(tope, experiencia) =>
			experiencia.startDate ? Math.max(tope, inicioDeMes(new Date(experiencia.startDate)).getTime()) : tope,
		inicioDeMes(new Date()).getTime(),
	);

	// ── 5. ListaDePlanes: los 3 más cercanos (ya vienen ordenados por `startDate` desde el backend) ──
	const proximas = items
		.filter(experiencia => !experiencia.startDate || new Date(experiencia.startDate) >= ahora)
		.slice(0, TOTAL_PLANES_MAS_CERCANOS);
	const etiquetasAcceso = etiquetaAccesoPorValor(t);
	const planesLista: PlanEnLista[] = proximas.map(experiencia => ({
		aviso: experiencia.viewerJoinStatus === 'REQUESTED' ? t('catalogo.planes.aviso.pedido') : undefined,
		cuando: experiencia.startDate ? formatearFechaHora(experiencia.startDate, i18n.language) : undefined,
		donde: formatearZona(experiencia.zone),
		estado: estadoPlanDesdeExperiencia(experiencia, ahora),
		etiquetaAcceso: etiquetasAcceso[experiencia.accessLabel],
		href: `/unirse/${experiencia.id}`,
		id: experiencia.id,
		organizador: construirOrganizador(experiencia),
		plazas: formatearPlazas(experiencia.confirmedCount, experiencia.capacity, t),
		precio: formatearPrecio(
			experiencia.priceCents,
			experiencia.currency,
			i18n.language,
			t('catalogo.planes.precioGratis'),
		),
		sello: datosSelloPlan(experiencia, ahora, t),
		titulo: experiencia.title,
		verFichaLabel: t('catalogo.planes.verFicha'),
	}));

	// ── 2. TablonDeCarteles: mes en curso, dos sellos ────────────────────────────────────────
	const puedeAvanzarTablon = mesTablon.getTime() < topeMes;
	const formateadorMesAnio = new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric' });
	const formateadorMesCorto = new Intl.DateTimeFormat(i18n.language, { month: 'short' });

	const filaDesdeExperiencia = (experiencia: ExperienceResponse): TablonDeCartelesFila => {
		const fecha = new Date(experiencia.startDate as string);
		const datosSello = datosSelloPlan(experiencia, ahora, t);
		return {
			clave: experiencia.id,
			dia: String(fecha.getDate()),
			donde: formatearZona(experiencia.zone),
			matasellos: formateadorMesCorto.format(fecha),
			onAbrir: () => irAFicha(experiencia.id),
			que: experiencia.title,
			sello: datosSello ? (
				<SelloEstado
					figura={datosSello.figura}
					mio={datosSello.mio}
					tamano='sm'
					texto={datosSello.texto}
					voz={datosSello.voz}
				/>
			) : undefined,
		};
	};

	const experienciasDelMesTablon = items.filter(
		experiencia => experiencia.startDate && mismoMes(new Date(experiencia.startDate), mesTablon),
	);
	const filasOficiales = experienciasDelMesTablon
		.filter(experiencia => esExperienciaOficial(experiencia.accessLabel))
		.map(filaDesdeExperiencia);
	const filasMiembros = experienciasDelMesTablon
		.filter(experiencia => experiencia.accessLabel === 'MEMBERS_ONLY')
		.map(filaDesdeExperiencia);
	const sellosTablon: TablonDeCartelesSello[] = [
		{
			cinta: t('catalogo.tablon.sellos.oficiales'),
			clave: 'oficiales',
			filas: filasOficiales,
			tono: 'oficiales',
			vacio: filasOficiales.length === 0 ? t('catalogo.tablon.vacio') : undefined,
		},
		{
			cinta: t('catalogo.tablon.sellos.miembros'),
			clave: 'miembros',
			filas: filasMiembros,
			tono: 'miembros',
			vacio: filasMiembros.length === 0 ? t('catalogo.tablon.vacio') : undefined,
		},
	];

	// ── 8. CarteleraDePlanes: hasta ocho, en el mismo orden que el listado ───────────────────
	const planesCartelera: PlanEnCartelera[] = items.slice(0, TOTAL_PLANES_CARTELERA).map(experiencia => ({
		closeLabel: t('catalogo.cartelera.cerrar'),
		ctaLabel: t('catalogo.cartelera.verMas'),
		dateRange: formatearRangoFechas(experiencia.startDate, experiencia.endDate, i18n.language),
		description: experiencia.description ?? undefined,
		fecha: experiencia.startDate,
		href: `/unirse/${experiencia.id}`,
		id: experiencia.id,
		openLabel: t('catalogo.cartelera.abrir', { titulo: experiencia.title }),
		pieCuando: experiencia.startDate ? formatearDiaCorto(experiencia.startDate, i18n.language) : '',
		plazas: formatearPlazas(experiencia.confirmedCount, experiencia.capacity, t),
		sello: datosSelloPlan(experiencia, ahora, t),
		title: experiencia.title,
	}));

	// ── 10. CalendarioDePlanes: rejilla del mes en curso, con agregados reales ───────────────
	const puedeAvanzarCalendario = mesCalendario.getTime() < topeMes;
	const rejillaCalendario = construirRejillaDelMes(mesCalendario);
	const agregadosPorDia = new Map<string, { confirmados: number; mios: number; total: number }>();
	for (const experiencia of items) {
		if (!experiencia.startDate) {
			continue;
		}
		const clave = formatearClaveIsoLocal(new Date(experiencia.startDate));
		const actual = agregadosPorDia.get(clave) ?? { confirmados: 0, mios: 0, total: 0 };
		actual.total += 1;
		actual.confirmados += experiencia.confirmedCount;
		if (experiencia.viewerJoinStatus === 'CONFIRMED') {
			actual.mios += 1;
		}
		agregadosPorDia.set(clave, actual);
	}
	const primerIdPorDia = new Map<string, string>();
	for (const experiencia of items) {
		if (!experiencia.startDate) {
			continue;
		}
		const clave = formatearClaveIsoLocal(new Date(experiencia.startDate));
		if (!primerIdPorDia.has(clave)) {
			primerIdPorDia.set(clave, experiencia.id);
		}
	}
	const hoyClave = formatearClaveIsoLocal(new Date());
	const hoyInicio = inicioDeHoy();
	const formateadorFechaLarga = new Intl.DateTimeFormat(i18n.language, {
		day: 'numeric',
		month: 'long',
		weekday: 'long',
	});

	const diasCalendario: DiaCalendarioDePlanes[] = rejillaCalendario.map(dia => {
		if (dia.deOtroMes) {
			return { clave: dia.clave, deOtroMes: true, estado: 'normal', etiquetaAccesible: '', numero: dia.numero };
		}
		const agregado = agregadosPorDia.get(dia.clave);
		let estado: EstadoDiaCalendarioDePlanes = 'normal';
		if (agregado && agregado.mios > 0) {
			estado = 'mio';
		} else if (agregado && dia.fecha < hoyInicio) {
			estado = 'pasado';
		} else if (dia.clave === hoyClave) {
			estado = 'hoy';
		} else if (agregado) {
			estado = 'ocupado';
		}

		const fechaLarga = formateadorFechaLarga.format(dia.fecha);
		let descripcion: string;
		if (agregado) {
			const partes: string[] = [t('catalogo.calendario.dia.planes', { count: agregado.total })];
			if (agregado.mios > 0) {
				partes.push(t('catalogo.calendario.dia.tuyos', { count: agregado.mios }));
			}
			if (agregado.confirmados > 0) {
				partes.push(t('catalogo.calendario.dia.apuntados', { count: agregado.confirmados }));
			}
			descripcion = partes.join(', ');
		} else {
			descripcion = t('catalogo.calendario.dia.sinPlanes');
		}

		return { clave: dia.clave, estado, etiquetaAccesible: `${fechaLarga}, ${descripcion}`, numero: dia.numero };
	});

	return (
		<div className='flex flex-col gap-8'>
			<IndiceDeVistas
				etiquetaNav={t('catalogo.indice.etiquetaNav')}
				vistas={[
					{ ancla: 'pl-tablon', etiqueta: t('catalogo.indice.vistas.tablon') },
					{ ancla: 'pl-planes', etiqueta: t('catalogo.indice.vistas.planes') },
					{ ancla: 'pl-cartelera', etiqueta: t('catalogo.indice.vistas.cartelera') },
					{ ancla: 'pl-mapa', etiqueta: t('catalogo.indice.vistas.mapa') },
					{ ancla: 'pl-calendario', etiqueta: t('catalogo.indice.vistas.calendario') },
				]}
			/>

			<div aria-busy={actualizandoCatalogo} className='scroll-mt-24' id='pl-tablon'>
				<TablonDeCarteles
					etiquetaMes={formateadorMesAnio.format(mesTablon)}
					etiquetaMesAnterior={t('catalogo.mes.irA', { mes: formateadorMesAnio.format(sumarMeses(mesTablon, -1)) })}
					etiquetaMesSiguiente={t('catalogo.mes.irA', { mes: formateadorMesAnio.format(sumarMeses(mesTablon, 1)) })}
					etiquetaSeccion={t('catalogo.tablon.titulo')}
					notaFinal={t('catalogo.tablon.notaFinal')}
					onMesAnterior={() => establecerMesTablon(anterior => sumarMeses(anterior, -1))}
					onMesSiguiente={
						puedeAvanzarTablon ? () => establecerMesTablon(anterior => sumarMeses(anterior, 1)) : undefined
					}
					sellos={sellosTablon}
					titulo={t('catalogo.tablon.titulo')}
				/>
			</div>

			<header className='flex flex-col gap-2'>
				<h1 className='font-display font-black text-3xl text-ink'>{t('catalogo.titulo')}</h1>
				<p className='text-base text-gray-ink'>
					{t('catalogo.subtitulo')} <em>{t('catalogo.subtituloEnfasis')}</em>
				</p>
				{/* Señal EN LÍNEA de actualización (filtro cambiado con el catálogo ya en pantalla): nunca
				sustituye el árbol, solo aparece mientras `actualizandoCatalogo` es cierto. `role='status'`
				+ `aria-live='polite'` para que un lector de pantalla la anuncie sin interrumpir. */}
				{actualizandoCatalogo ? (
					<p aria-live='polite' className='text-gray-ink text-sm' role='status'>
						{t('catalogo.estados.actualizando')}
					</p>
				) : null}
				{/* El agujero de `keepPreviousData` (oleada 2, corrección de rate limiting): la
				actualización EN LÍNEA de arriba puede acabar en error sin tocar `data` — ver el JSDoc
				de `errorActualizacionCatalogo`. Mismo criterio de "nunca sustituye el árbol", solo que
				esta vez el aviso se queda (no desaparece con el tiempo) hasta que un cambio de filtro
				lo resuelva. */}
				{!actualizandoCatalogo && errorActualizacionCatalogo !== undefined ? (
					<p aria-live='polite' className={`text-sm ${CLASE_TEXTO_ERROR}`} role='status'>
						{errorActualizacionCatalogo}
					</p>
				) : null}
			</header>

			<FiltrosCatalogo ejes={ejes} onCambio={manejarCambioFiltro} titulo={t('catalogo.filtros.titulo')} />

			<ListaDePlanes
				aria-busy={actualizandoCatalogo}
				className='scroll-mt-24'
				id='pl-planes'
				planes={planesLista}
				textoVacio={t('catalogo.planes.vacio')}
				titulo={t('catalogo.planes.titulo')}
			/>

			<MantenerPulsado
				etiqueta={t('catalogo.mantener.etiqueta')}
				nota={t('catalogo.mantener.nota')}
				onCompletado={() => establecerCarteleraRevelada(true)}
			/>

			<div aria-busy={actualizandoCatalogo} className='scroll-mt-24' id='pl-cartelera'>
				{carteleraRevelada ? (
					<CarteleraDePlanes
						etiquetaSeccion={t('catalogo.cartelera.titulo')}
						planes={planesCartelera}
						subtitulo={t('catalogo.cartelera.subtitulo')}
						titulo={t('catalogo.cartelera.titulo')}
					/>
				) : null}
			</div>

			<div className='scroll-mt-24' id='pl-mapa'>
				<MapaDePlanes
					etiquetaCanarias={t('catalogo.mapa.etiquetaCanarias')}
					leyenda={leyendaMapa}
					nota={notaMapa}
					onSeleccionarProvincia={clave => {
						const region = NOMBRE_POR_CLAVE_PROVINCIA.get(clave);
						if (region) {
							manejarCambioFiltro('region', region);
						}
					}}
					provincias={provinciasParaElMapa}
					titulo={t('catalogo.mapa.titulo')}
					tituloAccesibleMapa={t('catalogo.mapa.tituloAccesible')}
				/>
			</div>

			<div aria-busy={actualizandoCatalogo} className='scroll-mt-24' id='pl-calendario'>
				{/* `CalendarioDePlanes` no pinta su propio titular (a diferencia de `TablonDeCarteles`/
				`ListaDePlanes`/`CarteleraDePlanes`/`MapaDePlanes`, que sí llevan `titulo`): lo añade quien
				monta este bloque, mismo criterio que el resto de secciones. */}
				<h2 className='mb-3 font-display font-bold text-ink text-xl leading-tight'>
					{t('catalogo.calendario.titulo')}
				</h2>
				<CalendarioDePlanes
					dias={diasCalendario}
					etiquetaMes={formateadorMesAnio.format(mesCalendario)}
					etiquetaMesAnterior={t('catalogo.mes.irA', { mes: formateadorMesAnio.format(sumarMeses(mesCalendario, -1)) })}
					etiquetaMesSiguiente={t('catalogo.mes.irA', { mes: formateadorMesAnio.format(sumarMeses(mesCalendario, 1)) })}
					etiquetaTabla={t('catalogo.calendario.etiquetaTabla', { mes: formateadorMesAnio.format(mesCalendario) })}
					nombresDeDia={nombresDeDiaSemana(i18n.language)}
					onMesAnterior={() => establecerMesCalendario(anterior => sumarMeses(anterior, -1))}
					onMesSiguiente={
						puedeAvanzarCalendario ? () => establecerMesCalendario(anterior => sumarMeses(anterior, 1)) : undefined
					}
					onSeleccionarDia={clave => {
						const id = primerIdPorDia.get(clave);
						if (id) {
							irAFicha(id);
						}
					}}
				/>
			</div>

			<p className='text-gray-ink text-sm leading-relaxed'>{t('catalogo.cierre')}</p>
		</div>
	);
}
